/**
 * Bubble layout geometry, shared by the physics and the saved-layout check.
 *
 * A bubble's shape is its disc plus, for a parent bubble, its title: curved
 * text on the top arc, just OUTSIDE the rim (render.js). Siblings keep their
 * whole shapes apart, so a title never runs across a neighbour, and a child
 * keeps its shape inside its parent's rim, so a nested title never crosses
 * its parent's title. The physics pushes shapes apart with soft forces (so
 * bubbles glide) and enforces the constraints on positions when it comes to
 * rest (enforceConstraints), so no overlap is left behind.
 *
 * Coordinates are screen-oriented (y down) and relative to the parent's
 * centre; titles are centred straight up. No DOM access here.
 */

/** Free space kept between sibling shapes. */
export const SIBLING_GAP = 10;
/** Space between a child's disc and its parent's rim. */
export const CONTAIN_MARGIN = 6;
/** Space between a child's title and its parent's rim (or its parent's title). */
export const TITLE_RIM_MARGIN = 6;

/** A title may run along up to 2/3 of the rim, centred on top (±120°). */
export const TITLE_MAX_HALF_ANGLE = (2 * Math.PI) / 3;

/**
 * A parent's title size in the map's units: 6% of the bubble's width, at
 * least 14. It is the room the layout keeps for the title, and the size it
 * is drawn at whatever the zoom (titles.js), so titles do not run over each
 * other and same-size bubbles show theirs at the same zoom (Alex,
 * 2026-10-16). Before: a tenth, 18 to 48: a large map's biggest bubbles had
 * no room for a title that could be read from far.
 * @param {number} size - the bubble's width
 */
export function titleFontSize(size) {
  return Math.max(14, size * 0.06);
}

/** Length of the arc a curved title may use (2/3 of the rim, a small margin kept). */
export function titleArcLength(textRadius) {
  return textRadius * 2 * TITLE_MAX_HALF_ANGLE * 0.97;
}

/**
 * Title band of a parent bubble.
 * @param {number} textRadius - radius of the text baseline arc
 * @param {number} fontSize
 * @param {number} textWidth - rendered width of the title text
 * @returns {{inner: number, outer: number, halfAngle: number}|null} radii
 *   the glyphs span (descenders dip below the baseline, possibly inside the
 *   rim) and half the angle they cover around straight up
 */
export function titleBand(textRadius, fontSize, textWidth) {
  if (!(textWidth > 0) || !(textRadius > 0)) return null;
  return {
    inner: textRadius - fontSize * 0.25,
    outer: textRadius + fontSize,
    halfAngle: Math.min(TITLE_MAX_HALF_ANGLE, (textWidth / 2 + fontSize * 0.25) / textRadius),
  };
}

// How far (radians) a direction with vertical component `uy` lies outside
// the title's span. The title is symmetric around straight up (0, -1).
function outsideTitle(title, uy) {
  return Math.max(0, Math.acos(Math.max(-1, Math.min(1, -uy))) - title.halfAngle);
}

/** Farthest reach of a bubble's shape from its centre along a unit direction (·, uy). */
export function reach(n, uy) {
  const t = n.title;
  if (!t) return n.radius;
  const delta = outsideTitle(t, uy);
  return delta >= Math.PI / 2 ? n.radius : Math.max(n.radius, t.outer * Math.cos(delta));
}

/** Largest reach in any direction (cheap rejection of distant pairs). */
export function maxReach(n) {
  return n.title ? Math.max(n.radius, n.title.outer) : n.radius;
}

/** Centre distance two siblings need along the unit direction from a to b (·, uy). */
export function separation(a, b, uy) {
  return reach(a, uy) + reach(b, -uy) + SIBLING_GAP;
}

/**
 * Farthest a child's centre may sit from its parent's centre in the unit
 * direction (·, uy), keeping its disc and its title inside the parent and
 * clear of the parent's own title, whose descenders dip inside the rim.
 * The title limit is exact for an arc: the farthest glyph point from the
 * parent's centre is the one angularly closest to the child's direction.
 */
export function maxCenterDistance(child, uy, parent) {
  const radius = innerRadius(parent);
  const titleEdge = parent.title ? parent.title.inner : Infinity;
  let limit = Math.min(radius - CONTAIN_MARGIN, titleEdge - CONTAIN_MARGIN) - child.radius;
  const t = child.title;
  if (t) {
    const room = Math.min(radius, titleEdge) - TITLE_RIM_MARGIN;
    const delta = outsideTitle(t, uy);
    const side = t.outer * Math.sin(delta);
    limit = Math.min(limit, room > side ? Math.sqrt(room * room - side * side) - t.outer * Math.cos(delta) : 0);
  }
  return Math.max(0, limit);
}

/** Inner radius children must stay within. */
export function innerRadius(parent) {
  return parent.bodyRadius ?? parent.radius;
}

/** How deep two sibling shapes overlap (0 when apart; the gap is not counted). */
export function shapeOverlap(a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.hypot(dx, dy);
  if (dist >= maxReach(a) + maxReach(b)) return 0;
  const uy = dist > 1e-9 ? dy / dist : 0;
  return Math.max(0, separation(a, b, uy) - SIBLING_GAP - dist);
}

/** How far a child's shape pokes out of its parent (0 when inside). */
export function escapeDistance(child, parent) {
  const dist = Math.hypot(child.x, child.y);
  if (dist <= 1e-9) return 0;
  return Math.max(0, dist - maxCenterDistance(child, child.y / dist, parent));
}

/**
 * Nodes overlapping a sibling by more than 1 px, each with its largest
 * overlap (rounded up).
 * @param {Array} nodes - physics nodes
 * @returns {Map<object, number>}
 */
export function residualOverlaps(nodes) {
  const groups = new Map();
  for (const n of nodes) {
    const key = n.parent || null;
    let group = groups.get(key);
    if (!group) groups.set(key, (group = []));
    group.push(n);
  }
  const left = new Map();
  for (const group of groups.values()) {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const overlap = shapeOverlap(group[i], group[j]);
        if (overlap <= 1) continue;
        const o = Math.ceil(overlap);
        left.set(group[i], Math.max(left.get(group[i]) || 0, o));
        left.set(group[j], Math.max(left.get(group[j]) || 0, o));
      }
    }
  }
  return left;
}

/**
 * One pass of the hard constraints over sibling groups: push overlapping
 * siblings apart, then bring escaped children back inside their parent.
 * `weight(n)` is how much a node yields: 0 keeps it in place (frozen by a
 * scoped simulation), otherwise overlaps are split in proportion.
 *
 * @param {Array<{parent: object|null, nodes: object[]}>} groups
 * @param {(n: object) => number} weight
 */
export function enforceConstraints(groups, weight) {
  for (const { parent, nodes } of groups) {
    separateSiblings(nodes, weight);
    if (parent) containChildren(parent, nodes, weight);
  }
}

function separateSiblings(nodes, weight) {
  const len = nodes.length;
  for (let i = 0; i < len; i++) {
    const a = nodes[i];
    const wa = weight(a);
    const aMax = maxReach(a);
    for (let j = i + 1; j < len; j++) {
      const b = nodes[j];
      const wb = weight(b);
      if (!(wa + wb)) continue;
      let dx = b.x - a.x;
      let dy = b.y - a.y;
      const far = aMax + maxReach(b) + SIBLING_GAP;
      let distSq = dx * dx + dy * dy;
      if (distSq >= far * far) continue;
      let dist = Math.sqrt(distSq);
      if (dist < 1e-6) {
        // Same centre (e.g. just reparented): pick a deterministic direction.
        const angle = (i * 31 + j) * 2.399963229728653;
        dx = Math.cos(angle);
        dy = Math.sin(angle);
        dist = 0;
      } else {
        dx /= dist;
        dy /= dist;
      }
      const push = separation(a, b, dy) - dist;
      if (push <= 0) continue;
      const share = push / (wa + wb);
      a.x -= dx * share * wa;
      a.y -= dy * share * wa;
      b.x += dx * share * wb;
      b.y += dy * share * wb;
    }
  }
}

function containChildren(parent, nodes, weight) {
  for (const n of nodes) {
    if (!weight(n)) continue;
    const dist = Math.hypot(n.x, n.y);
    if (dist <= 1e-9) continue;
    const max = maxCenterDistance(n, n.y / dist, parent);
    if (dist > max) {
      const scale = max / dist;
      n.x *= scale;
      n.y *= scale;
    }
  }
}
